<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ConversationApi from 'dashboard/api/conversations';
import Avatar from 'next/avatar/Avatar.vue';
import { escapeHtml } from 'shared/helpers/HTMLSanitizer';
import CaretAnchoredPicker from 'dashboard/components-next/preview-picker/CaretAnchoredPicker.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
  caretPosition: { type: Object, default: null },
  searchKey: { type: String, default: '' },
});
const emit = defineEmits(['selectAgent', 'close', 'removeTrigger']);
const { t } = useI18n();
const search = ref(props.searchKey);
const participants = ref([]);
const loading = ref(true);
const failed = ref(false);
const load = async () => {
  loading.value = true;
  failed.value = false;
  try {
    const { data } = await ConversationApi.getWhatsAppParticipants(
      props.conversationId
    );
    participants.value = data.participants;
  } catch {
    failed.value = true;
  } finally {
    loading.value = false;
  }
};
onMounted(load);
const items = computed(() => {
  const query = search.value.trim().toLocaleLowerCase();
  return participants.value
    .filter(member =>
      `${member.name} ${member.phone_number || ''}`
        .toLocaleLowerCase()
        .includes(query)
    )
    .map(member => ({
      id: member.jid,
      label: member.name,
      title: escapeHtml(member.name),
      subtitle: member.phone_number,
      record: { id: member.jid, name: member.name, type: 'whatsapp' },
    }));
});
const emptyLabel = computed(() => {
  if (loading.value) return t('CONVERSATION.WHATSAPP_MENTIONS.LOADING');
  if (failed.value) return t('CONVERSATION.WHATSAPP_MENTIONS.ERROR');
  return t('CONVERSATION.WHATSAPP_MENTIONS.EMPTY');
});
</script>

<template>
  <CaretAnchoredPicker
    v-model:search="search"
    :caret-position="caretPosition"
    :items="items"
    :is-loading="loading"
    :search-placeholder="t('CONVERSATION.WHATSAPP_MENTIONS.SEARCH')"
    :empty-label="emptyLabel"
    @select="item => emit('selectAgent', item.record)"
    @close="emit('close')"
    @remove-trigger="emit('removeTrigger')"
  >
    <template #filters>
      <div
        class="flex items-center justify-between w-full gap-2 text-sm text-n-slate-12"
        aria-live="polite"
      >
        <span>{{ t('CONVERSATION.WHATSAPP_MENTIONS.TITLE') }}</span>
        <button
          v-if="failed"
          type="button"
          class="px-2 py-1 rounded text-n-blue-text hover:bg-n-alpha-1"
          @click="load"
        >
          {{ t('CONVERSATION.WHATSAPP_MENTIONS.RETRY') }}
        </button>
      </div>
    </template>
    <template #leading="{ item }">
      <Avatar :name="item.label" :size="24" rounded-full />
    </template>
  </CaretAnchoredPicker>
</template>
